import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Label as RoccoLabel } from '@rocco/components/label';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    isMultipleValuesFilterState,
    isSearchQueryFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import type { FilterStates } from '@rocco/ui/filter-sort/view-models/filter-states';
import { useAtom } from 'jotai';
import { uiStateAtomFiltersModalIsOpened } from '../../filter-sort/states/ui-states-filters-modal';

interface NoMatchesFoundProps {
    hideFilters?: boolean;
    filterStates: FilterStates;
    onDisableFilter: (
        key: string,
        state: FilterState,
        listItem?: SingleValueFilterState,
    ) => void;
    onResetFilter: () => void;
}

export const NoMatchesFound = (props: NoMatchesFoundProps) => {
    const {
        hideFilters = false,
        filterStates,
        onDisableFilter,
        onResetFilter,
    } = props;

    const [, setUiStateFiltersModalIsOpened] = useAtom(
        uiStateAtomFiltersModalIsOpened,
    );

    return (
        <div className="mb-4">
            <span className="mt-3 block py-0 text-base tracking-normal md:mt-2.5 md:py-4 md:text-2xl md:tracking-wide">
                Sorry, 0 results found.
                <br />
                Try changing the filters or search term.
            </span>

            {!hideFilters && (
                <>
                    <div className="mt-6 flex flex-wrap gap-1 md:mt-2.5 md:gap-2">
                        {Object.entries(filterStates.states).map(
                            ([key, state]) => {
                                if (!state) return null;

                                if (
                                    isSingleValueFilterState(state) &&
                                    state.checked
                                ) {
                                    return (
                                        <NoMatchesFoundFilterOption
                                            key={key}
                                            label={state.label ?? ''}
                                            onDisable={() => {
                                                onDisableFilter(key, state);
                                            }}
                                        />
                                    );
                                } else if (
                                    isMultipleValuesFilterState(state) &&
                                    state.selectedItems.length > 0
                                ) {
                                    return state.selectedItems.map(
                                        (item, index) => (
                                            <NoMatchesFoundFilterOption
                                                key={`${key}-${index}`}
                                                label={item.label ?? ''}
                                                onDisable={() => {
                                                    onDisableFilter(
                                                        key,
                                                        state,
                                                        item,
                                                    );
                                                }}
                                            />
                                        ),
                                    );
                                } else if (
                                    isSearchQueryFilterState(state) &&
                                    state.query.length > 0
                                ) {
                                    return (
                                        <NoMatchesFoundFilterOption
                                            key={key}
                                            label={state.query}
                                            onDisable={() => {
                                                onDisableFilter(key, state);
                                            }}
                                        />
                                    );
                                }

                                return null;
                            },
                        )}
                    </div>

                    <div className="mt-6 flex gap-2 md:mt-7">
                        <button
                            className="text-sm underline"
                            onClick={() => setUiStateFiltersModalIsOpened(true)}
                        >
                            Show filters
                        </button>

                        <div className="text-sm">•</div>

                        <button
                            className="text-sm underline"
                            onClick={() => onResetFilter()}
                        >
                            Clear all
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

interface NoMatchesFoundFilterOptionProps {
    label: string;
    onDisable: () => void;
}

const NoMatchesFoundFilterOption = (props: NoMatchesFoundFilterOptionProps) => {
    const { label, onDisable } = props;

    return (
        <div className="flex items-center gap-2 rounded-2 border border-gray-900 px-3 py-2 md:gap-0">
            <RoccoCheckbox
                id={label}
                checked={true}
                onCheckedChange={onDisable}
            />
            <RoccoLabel htmlFor={label} target="checkbox" singleLine>
                {label}
            </RoccoLabel>
        </div>
    );
};
